import React, { useContext, useEffect, useState } from "react";
import { CloseButton } from "react-bootstrap";
import Modal from "react-modal";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import { getForwardableQueryString } from "../../stepper/resumeApplication";

/**
 * PD-6: post-submit confirmation — V3 design (single card: reference,
 * 3-stage timeline, named funding specialist, PDF download). Rendered by
 * StepShell.js inside the same shell/topbar/aside chrome every other step
 * uses (see its own `submitted` branch), not a standalone page anymore.
 *
 * PD-22: the funding specialist card now reads the real Salesforce
 * Lead/Opportunity Owner (name/email/phone) from
 * `state.questions.fundingSpecialist`, set by StepReview.js from the
 * submit-application response's `funding_specialist` field. That field is
 * only populated once the ASYNC Salesforce sync (management/commands/sync.py)
 * has already run against this applicant's PartialApplication before they
 * submitted — for a brand-new lead it's frequently still null at this exact
 * moment (the sync isn't triggered by submission itself), so this renders a
 * generic "assigned shortly" message instead of a name in that case, rather
 * than fabricating a person.
 *
 * PD-32: that snapshot is taken once, at submit time, and never refreshed —
 * so a specialist assigned moments later (once the sync actually runs) would
 * otherwise never surface for this borrower. The fallback card's "Check
 * status" button re-checks live via ApplicationFundingSpecialistStatusView
 * (keyed on the *final* Application.uuid — StepReview.js overwrites
 * state.applicationUUID with it on submit, replacing the deleted
 * PartialApplication's uuid) and swaps in the specialist card if it's now
 * populated.
 */
function initialsOf(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

const ThanksComponent = ({ setProgress }) => {
  const { state } = useContext(GlobalContext);

  useEffect(() => {
    setProgress?.(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.questions]);

  const signedPdfUrl = state?.questions?.signedPdfUrl;
  const handleDownloadPdf = () => {
    if (signedPdfUrl) {
      window.open(signedPdfUrl, "_blank");
    }
  };

  // Keeps the original third-party query params (Soarr/Purple Wave
  // price, year, make, model, ...) so the next application is pre-filled
  // the same way — only our own `uuid` is dropped, or a resumed-then-
  // submitted application's link would reopen that same application.
  const handleStartAnother = () => {
    const query = getForwardableQueryString(window.location.search);
    window.location.href = window.location.pathname + (query ? `?${query}` : "");
  };

  const referenceNumber = state?.applicationUUID
    ? state.applicationUUID.slice(0, 8).toUpperCase()
    : "PENDING";

  // Local copy, seeded from the submit-time snapshot — "Check status" below
  // updates this directly rather than round-tripping through GlobalContext,
  // since nothing else on the app needs this once the borrower has landed
  // here.
  const [fundingSpecialist, setFundingSpecialist] = useState(
    () => state?.questions?.fundingSpecialist || null
  );
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusCheckedEmpty, setStatusCheckedEmpty] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleCheckStatus = async () => {
    if (!state?.applicationUUID || isCheckingStatus) return;
    setIsCheckingStatus(true);
    setStatusCheckedEmpty(false);
    try {
      const res = await axios.get(
        `lender/application/funding-specialist-status/?uuid=${state.applicationUUID}`
      );
      const specialist = res?.data?.funding_specialist;
      if (specialist?.name) {
        setFundingSpecialist(specialist);
      } else {
        setStatusCheckedEmpty(true);
      }
    } catch (err) {
      console.log("error checking funding specialist status", err);
      setStatusCheckedEmpty(true);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const hasEmail = !!fundingSpecialist?.email;
  const hasPhone = !!fundingSpecialist?.phone;
  const mailtoHref = hasEmail
    ? `mailto:${fundingSpecialist.email}?subject=${encodeURIComponent(
        `Application PA-${referenceNumber}`
      )}`
    : null;
  const telHref = hasPhone ? `tel:${fundingSpecialist.phone}` : null;

  const signedAt = new Date().toLocaleDateString([], { month: "long", day: "numeric" });

  // Client-requested copy (wording only — still a static 3-stage timeline,
  // not yet wired to the real Application.status; see PD ticket for the
  // follow-up to make this reflect live status if the client wants that).
  const timelineSteps = [
    {
      title: "Signed",
      description: `Received ${signedAt}.`,
      status: "done",
    },
    {
      title: "Under Review",
      description: "Our team will review your application and reach out if we need anything else.",
      status: "active",
    },
    {
      title: "Approved",
      description: "Your Representative will provide you with approval options for this purchase.",
      status: "upcoming",
    },
  ];

  return (
    <>
      <div className="pd6-received__eyebrow">Submitted</div>

      <span className="pd6-received__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>

      <h2 className="pd6-step__title">Received</h2>
      <p className="pd6-step__helper" style={{ borderBottom: "none", paddingBottom: 0 }}>
        Your signed application is in.
      </p>

      <div className="pd6-received__reference">
        <span>Reference</span>
        <strong>PA-{referenceNumber}</strong>
      </div>

      <div className="pd6-received__timeline">
        {timelineSteps.map((step, index) => (
          <div className="pd6-received__timeline-row" key={step.title}>
            <div className="pd6-received__timeline-marker">
              <span
                className={`pd6-received__timeline-index pd6-received__timeline-index--${step.status}`}
              >
                {step.status === "done" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              {index < timelineSteps.length - 1 && <span className="pd6-received__timeline-line" />}
            </div>
            <div className="pd6-received__timeline-copy">
              <strong>{step.title}</strong>
              <p>{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {fundingSpecialist?.name ? (
        <div className="pd6-specialist-card">
          <span className="pd6-specialist-card__avatar">{initialsOf(fundingSpecialist.name)}</span>
          <div className="pd6-specialist-card__info">
            <div className="pd6-specialist-card__name">{fundingSpecialist.name}</div>
            <div className="pd6-specialist-card__role">Your MAZO Representative</div>
          </div>
          <div className="pd6-specialist-card__actions">
            {hasEmail && hasPhone ? (
              // Both channels available — let the borrower pick one instead
              // of showing two separate buttons.
              <button
                type="button"
                className="pd6-btn pd6-btn--primary pd6-btn--small"
                onClick={() => setIsContactModalOpen(true)}
              >
                Send a message
              </button>
            ) : (
              // Only one channel on file — act on it directly, no picker.
              <a className="pd6-btn pd6-btn--primary pd6-btn--small" href={mailtoHref || telHref}>
                Send a message
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="pd6-specialist-card">
          <div className="pd6-specialist-card__info">
            <div className="pd6-specialist-card__name">Our team</div>
            <div className="pd6-specialist-card__role">
              A MAZO Representative will be assigned to your application shortly (~2 minutes).
            </div>
            {statusCheckedEmpty && (
              <div className="pd6-specialist-card__status-note">
                Still not assigned yet — try again in a bit.
              </div>
            )}
          </div>
          <div className="pd6-specialist-card__actions">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost pd6-btn--small"
              onClick={handleCheckStatus}
              disabled={isCheckingStatus}
            >
              {isCheckingStatus ? "Checking..." : "Check status"}
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isContactModalOpen}
        onRequestClose={() => setIsContactModalOpen(false)}
        contentLabel="Choose how to contact your funding specialist"
        ariaHideApp={false}
        className="pd6-contact-modal"
        overlayClassName="pd6-contact-modal-overlay"
      >
        <div className="pd6-contact-modal__header">
          <h4 className="pd6-contact-modal__title">Contact {fundingSpecialist?.name}</h4>
          <CloseButton onClick={() => setIsContactModalOpen(false)} />
        </div>
        <div className="pd6-contact-modal__options">
          <a
            className="pd6-contact-modal__option"
            href={mailtoHref}
            onClick={() => setIsContactModalOpen(false)}
          >
            <span className="pd6-contact-modal__option-label">Email</span>
            <span className="pd6-contact-modal__option-detail">{fundingSpecialist?.email}</span>
          </a>
          <a
            className="pd6-contact-modal__option"
            href={telHref}
            onClick={() => setIsContactModalOpen(false)}
          >
            <span className="pd6-contact-modal__option-label">Phone</span>
            <span className="pd6-contact-modal__option-detail">{fundingSpecialist?.phone}</span>
          </a>
        </div>
      </Modal>

      <div className="pd6-received__footer">
        <button
          type="button"
          className="pd6-btn pd6-btn--ghost pd6-btn--small"
          onClick={handleDownloadPdf}
          disabled={!signedPdfUrl}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M4 19h16" />
          </svg>
          <span>Download signed PDF</span>
        </button>
        <button
          type="button"
          className="pd6-btn pd6-btn--ghost pd6-btn--small"
          onClick={handleStartAnother}
        >
          Start another application
        </button>
      </div>
    </>
  );
};

export default ThanksComponent;
